import type {
  Coding,
  QuestionnaireItem,
  QuestionnaireItemAnswerOption,
  QuestionnaireResponseItemAnswer,
} from '@medplum/fhirtypes';
import { getItemAnswerOptionValue } from '@medplum/react-hooks';

/**
 * Primary label for a QuestionnaireItemAnswerOption — aligned with
 * `TemplatePreviewDrawer` (valueString, then coding display, then code).
 */
export function getAnswerOptionDisplayLabel(opt: QuestionnaireItemAnswerOption): string {
  return opt.valueString ?? opt.valueCoding?.display ?? opt.valueCoding?.code ?? '';
}

function answerOptionToResponseAnswer(
  option: QuestionnaireItemAnswerOption,
): QuestionnaireResponseItemAnswer {
  const typed = getItemAnswerOptionValue(option);
  if (typed.type === 'Coding' && typed.value) {
    return { valueCoding: typed.value as Coding };
  }
  if (typed.type === 'string' && typed.value !== undefined) {
    return { valueString: String(typed.value) };
  }
  return { valueString: getAnswerOptionDisplayLabel(option) };
}

/** Map a rendered choice label back to the FHIR answer shape (valueCoding when coded). */
export function buildChoiceAnswerFromLabel(
  item: QuestionnaireItem,
  label: string,
): QuestionnaireResponseItemAnswer {
  const options = item.answerOption ?? [];
  const match = options.find(
    (opt, idx) => (getAnswerOptionDisplayLabel(opt) || `Option ${idx + 1}`) === label,
  );
  if (match) return answerOptionToResponseAnswer(match);
  return { valueString: label };
}

export function buildChoiceAnswersFromLabels(
  item: QuestionnaireItem,
  labels: string[],
): QuestionnaireResponseItemAnswer[] {
  return labels.map((label) => buildChoiceAnswerFromLabel(item, label));
}

/**
 * Apply an edited label while preserving coded options (system/code) when the
 * option only had `valueCoding` (typical for scored measures from the pipeline).
 */
export function patchAnswerOptionDisplayLabel(
  prev: QuestionnaireItemAnswerOption,
  value: string,
): QuestionnaireItemAnswerOption {
  if (prev.valueString !== undefined && prev.valueString !== null) {
    return { ...prev, valueString: value };
  }
  if (prev.valueCoding) {
    return {
      ...prev,
      valueCoding: {
        ...prev.valueCoding,
        display: value,
      },
    };
  }
  return { ...prev, valueString: value };
}
